import { HttpClient } from '@angular/common/http';
import { Injectable, OnDestroy } from '@angular/core';
import { ResponseObjectSuccess } from '@interfaces/response-success';
import { filter, map, Observable, Subject, take, takeUntil } from 'rxjs';
import { Authentication, AuthenticationForm, Cuenta, ResetPasswordForm } from '@models/.';
import { environment } from '@environments/environment';
import { Credential } from '@core/models';
import { MenuItem } from 'primeng/api';
import { transformDropdown } from '@core/helpers';
import { selectAuthenticationPermissions, selectCredential } from '@store/selectors';
import { Store } from '@ngrx/store';
import { ModuleCodeEnum, RolModuleEnum, RolModulePermissionEnum } from '@core/enums';
import { hasPermission } from '@core/helpers/general.helper';
import { ChangePassword } from './interfaces/reset-password';
import { Response } from '@core/interfaces';

@Injectable({
  providedIn: 'root'
})
export class AuthService implements OnDestroy {
  credential$!: Observable<Credential | null>;
  permissions: (RolModuleEnum | RolModulePermissionEnum)[] = [];

  private destroy$ = new Subject<void>();

  constructor(
    private http: HttpClient,
    private store: Store,
  ) {
    this.credential$ = this.store.select(selectCredential);

    this.store.select(selectAuthenticationPermissions).pipe(
      filter((permissions) => !!permissions),
      takeUntil(this.destroy$)
    ).subscribe((permissions) => this.permissions = permissions);
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  authenticate(authenticationForm: AuthenticationForm): Observable<ResponseObjectSuccess<Authentication>> {
    return this.http.post<ResponseObjectSuccess<Authentication>>(
      `${environment.microSeguridades}/api/v1/pos/usuario/login`,
      authenticationForm,
    );
  }

  forgotPassword({ email }: ResetPasswordForm): Observable<Response> {
    return this.http.post<Response>(
      `${environment.microSeguridades}/api/v1/pos/usuario/resetPassword`,
      {
        correo: email,
        code: ModuleCodeEnum.JuvAdmin
      }
    );
  }

  changePassword({ token, password }: ChangePassword): Observable<Response> {
    return this.http.post<Response>(
      `${environment.microSeguridades}/api/v1/pos/usuario/changePassword`,
      { token, contrasena: password }
    );
  }

  getDropdown(): Observable<MenuItem[]> {
    return this.credential$.pipe(
      filter((credential): credential is Credential => !!credential && !!credential?.cuentas),
      map(({ cuentas: accounts }) => ({ accounts: accounts as Cuenta[] })),
      map(({ accounts }) => transformDropdown(accounts, 'idCuenta', 'nombre')),
    );
  }

  getAllPermissions(): (RolModuleEnum | RolModulePermissionEnum)[] {
    return this.permissions;
  }

  hasRole(code: string): boolean {
    return hasPermission(code as RolModuleEnum | RolModulePermissionEnum, this.permissions);
  }

  hasAnyRole(codes: string[]): boolean {
    return codes?.some((code) => hasPermission(code as RolModuleEnum | RolModulePermissionEnum, this.permissions));
  }

  hasEveryRole(codes: string[]): boolean {
    return codes?.every((code) => hasPermission(code as RolModuleEnum | RolModulePermissionEnum, this.permissions));
  }

  hasPermissionObservable(code: string): Observable<boolean> {
    return this.store.select(selectAuthenticationPermissions).pipe(
      filter((permissions) => !!permissions),
      map((permissions) => hasPermission(code as RolModuleEnum | RolModulePermissionEnum, permissions)),
      take(1)
    )
  }

  hasAnyPermissionObservable(codes: string[]): Observable<boolean> {
    return this.store.select(selectAuthenticationPermissions).pipe(
      filter((permissions) => !!permissions),
      map((permissions) => codes?.some((code) => hasPermission(code as RolModuleEnum | RolModulePermissionEnum, permissions))),
      take(1)
    )
  }

  getDefaultPage(): string {
    if (this.hasRole(RolModuleEnum.Account)) return '/account';
    if (this.hasRole(RolModuleEnum.Store)) return '/store';
    if (this.hasRole(RolModuleEnum.Role)) return '/role';
    if (this.hasRole(RolModuleEnum.User)) return '/user';

    return '/login';
  }
}
